import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Heatmap-style matrix grid — row headers down the left, column headers across
 * the top, and a grid of tone-colored pill cells in between. Reach for this
 * whenever you're cross-tabulating two dimensions (tier→tier transitions,
 * cohort→month, owner→stage) and want the magnitude *and* the meaning to read
 * at a glance.
 *
 *   <Matrix
 *     cornerLabel="from \ to"
 *     columns={[<TierChip tier={1} />, …]}
 *     rows={[<TierChip tier={1} />, …]}
 *     cells={grid}            // cells[rowIndex][colIndex]
 *   />
 *
 * Unlike status chips (light tints by default) the cells are deliberately solid
 * — a matrix is a data-viz heatmap, so saturated fills are doing the work of a
 * color scale rather than labelling a status. Empty / zero cells fall back to a
 * quiet `neutral` so the populated cells pop.
 */

export type MatrixTone = "brand" | "success" | "warning" | "danger" | "neutral";

export interface MatrixCell {
  /** Cell contents — usually a count, but any node works. */
  value: React.ReactNode;
  /** Color treatment. Defaults to `neutral` (a muted, empty-looking cell). */
  tone?: MatrixTone;
  /** Native title / tooltip for the cell. */
  title?: string;
  /** When set, the cell becomes a button (cursor + hover + focus ring). */
  onClick?: () => void;
  /** Draws a selection ring around the cell. Pair with `onClick`. */
  selected?: boolean;
}

export interface MatrixProps {
  /** Column header nodes, left → right. */
  columns: React.ReactNode[];
  /** Row header nodes, top → bottom. */
  rows: React.ReactNode[];
  /** Cell grid indexed `cells[rowIndex][colIndex]`. Missing cells render empty. */
  cells: MatrixCell[][];
  /** Optional label for the top-left corner cell. */
  cornerLabel?: React.ReactNode;
  className?: string;
}

const TONE_STYLES: Record<MatrixTone, string> = {
  brand: "bg-brand text-white",
  success: "bg-success text-success-foreground",
  warning: "bg-warning text-warning-foreground",
  danger: "bg-danger text-danger-foreground",
  neutral: "bg-muted text-muted-foreground",
};

export function Matrix({ columns, rows, cells, cornerLabel, className }: MatrixProps) {
  const cols = columns.length;
  return (
    // Presentational grid — a heatmap, not a data table. Interactive cells are
    // real <button>s (keyboard + screen-reader reachable via aria-label); the
    // rest are static. We intentionally skip ARIA table roles, which would
    // require a full grid/row/cell hierarchy this layout doesn't model.
    <div
      className={cn("grid items-stretch gap-1.5", className)}
      style={{
        gridTemplateColumns: `minmax(2.5rem, max-content) repeat(${cols}, minmax(0, 1fr))`,
      }}
    >
      {/* Header row: corner + column headers */}
      <div className="flex items-end justify-end px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {cornerLabel}
      </div>
      {columns.map((c, j) => (
        <div
          key={`col-${j}`}
          className="flex items-end justify-center px-2 pb-1 text-xs font-semibold text-muted-foreground"
        >
          {c}
        </div>
      ))}

      {/* Body rows */}
      {rows.map((rh, i) => (
        <React.Fragment key={`row-${i}`}>
          <div className="flex items-center justify-end px-2 text-xs font-semibold text-muted-foreground">
            {rh}
          </div>
          {columns.map((_, j) => {
            const cell = cells[i]?.[j] ?? { value: null };
            const tone = cell.tone ?? "neutral";
            const base = cn(
              "flex items-center justify-center rounded-lg px-3 py-3.5 text-center text-sm font-bold tabular-nums",
              TONE_STYLES[tone],
              cell.selected && "ring-2 ring-foreground ring-offset-2 ring-offset-card",
            );
            if (cell.onClick) {
              return (
                <button
                  key={`cell-${i}-${j}`}
                  type="button"
                  title={cell.title}
                  aria-label={cell.title}
                  aria-pressed={cell.selected}
                  onClick={cell.onClick}
                  className={cn(
                    base,
                    "cursor-pointer transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                  )}
                >
                  {cell.value}
                </button>
              );
            }
            return (
              <div key={`cell-${i}-${j}`} title={cell.title} className={base}>
                {cell.value}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}
